import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useClient } from '@/contexts/ClientContext';

import { CoryBubble } from './cory/CoryBubble';
import { CoryHeader } from './cory/CoryHeader';
import { CoryMessageList } from './cory/CoryMessageList';
import { CoryTicketForm } from './cory/CoryTicketForm';
import { CoryInputBar } from './cory/CoryInputBar';
import { panelStyle } from './cory/coryStyles';
import { fetchSessionMessages, postChatMessage, submitCoryTicket } from './cory/coryApi';
import { captureDiagnostics } from './cory/coryDiagnostics';
import { authFetch } from '@/utils/authFetch';
import { API_URL } from '@/config/apiConfig';
import { toast } from '@/utils/safeToast';

function CoryChat() {
  const { user } = useAuth();
  const { customerBusiness } = useClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showTicketForm, setShowTicketForm] = useState(false);
  const [ticketData, setTicketData] = useState({ subject: '', description: '', priority: 'standard' });
  const [ticketScreenshots, setTicketScreenshots] = useState([]);
  const [deflectionAnswered, setDeflectionAnswered] = useState(() => new Set());
  const [hovered, setHovered] = useState(false);
  const [proactiveHint, setProactiveHint] = useState(null);
  const [coryStatus, setCoryStatus] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);
  useEffect(() => { if (open && inputRef.current) inputRef.current.focus(); }, [open]);

  // Cory plan/usage snapshot — drives the free→paid temptation in the panel.
  useEffect(() => {
    if (open && user) {
      authFetch(`${API_URL}/api/cory/status`)
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => d && setCoryStatus(d))
        .catch(() => {});
    }
  }, [open, user]);

  useEffect(() => {
    if (open && !sessionId && user) {
      const stored = localStorage.getItem('cory_session_id');
      if (stored) {
        setSessionId(stored);
        fetchSessionMessages(stored)
          .then(setMessages)
          .catch((err) => console.error('Failed to load Cory messages:', err));
      }
    }
  }, [open, sessionId, user]);

  // Proactive insight when business switches
  const lastBusinessRef = useRef(customerBusiness?.business_id);
  useEffect(() => {
    const currentBizId = customerBusiness?.business_id;
    if (currentBizId && currentBizId !== lastBusinessRef.current) {
      lastBusinessRef.current = currentBizId;
      const bizName = customerBusiness?.name || 'this business';
      if (open && user) {
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: `I've switched context to **${bizName}**. I now have access to ${bizName}'s live data — deals, pipeline, contacts, and revenue metrics. Ask me anything about ${bizName}'s performance!`,
            timestamp: new Date().toISOString(),
            isProactive: true,
          },
        ]);
      }
      if (!open) setProactiveHint(`Switched to ${bizName}`);
    }
  }, [customerBusiness?.business_id, open, customerBusiness?.name, user]);

  const sendMessage = async (e) => {
    e?.preventDefault();
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: userMsg, timestamp: new Date().toISOString() }]);
    setLoading(true);
    try {
      const data = await postChatMessage({
        message: userMsg,
        sessionId,
        business: customerBusiness,
        diagnostics: captureDiagnostics(),
      });
      if (!sessionId) {
        setSessionId(data.session_id);
        localStorage.setItem('cory_session_id', data.session_id);
      }
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: data.response,
          timestamp: new Date().toISOString(),
          suggested_modules: data.suggested_modules,
          is_ticket_suggestion: data.is_ticket_suggestion,
          sources: data.sources,
          upsell_modules: data.upsell_modules,
          limit_reached: data.limit_reached,
          messages_remaining: data.messages_remaining,
          tier: data.tier,
          deflection: data.deflection,
        },
      ]);
      // Only auto-open the ticket form when we DIDN'T deflect with a known fix.
      if (data.is_ticket_suggestion && !data.deflection) setShowTicketForm(true);
      // Keep the usage meter live as the allowance is spent.
      if (data.tier !== undefined) {
        setCoryStatus((prev) =>
          prev ? { ...prev, tier: data.tier ?? prev.tier, remaining: data.messages_remaining } : prev
        );
      }
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: "I'm having a brief connection issue. Please try again.",
          timestamp: new Date().toISOString(),
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeflectionOutcome = async (issueId, resolved) => {
    setDeflectionAnswered((prev) => new Set(prev).add(issueId));
    try {
      const res = await authFetch(`${API_URL}/api/cory/deflection/outcome`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ issue_id: issueId, session_id: sessionId, resolved }),
      });
      const data = await res.json().catch(() => ({}));
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: data.message || (resolved ? 'Glad that helped!' : "I've raised this with our tech team."),
          timestamp: new Date().toISOString(),
        },
      ]);
    } catch {
      toast.error('Could not record that — please try again.');
    }
  };

  const handleUpgrade = useCallback(() => {
    const target = coryStatus?.upgrade_url || '/app/settings/subscription';
    setOpen(false);
    navigate(target);
  }, [coryStatus, navigate]);

  const submitTicket = async () => {
    if (!ticketData.subject.trim() || !ticketData.description.trim()) return;
    setLoading(true);
    try {
      const data = await submitCoryTicket({
        ticketData,
        sessionId,
        screenshots: ticketScreenshots,
        diagnostics: captureDiagnostics(),
      });
      if (data) {
        const shotNote = ticketScreenshots.length
          ? `\n${ticketScreenshots.length} screenshot(s) + your device details were attached so we can reproduce it.`
          : '';
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: `Ticket created: ${data.ticket_id}\nSubject: ${data.subject}\nPriority: ${data.priority === 'priority' ? 'Priority ($149 — 7hr SLA)' : 'Standard'}\nStatus: ${data.status}${shotNote}\n\nOur team will follow up.`,
            timestamp: new Date().toISOString(),
          },
        ]);
        setShowTicketForm(false);
        setTicketData({ subject: '', description: '', priority: 'standard' });
        setTicketScreenshots([]);
      }
    } catch { /* swallowed — non-actionable */ } finally {
      setLoading(false);
    }
  };

  const startNewSession = () => {
    setMessages([]);
    setSessionId(null);
    localStorage.removeItem('cory_session_id');
    setShowTicketForm(false);
    setTicketScreenshots([]);
  };

  // One-tap: add several add-ons together (e.g. a module + Cory+) so the user
  // doesn't have to upgrade multiple times.
  const addBundle = async (mods) => {
    if (!mods?.length) return;
    if (mods.length === 1) return addModule(mods[0]);
    let ok = 0;
    for (const mod of mods) {
      try {
        const res = await authFetch(`${API_URL}/api/account/add-ons/purchase`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ add_on_type: 'module', module_id: mod.module_id, quantity: 1 }),
        });
        const data = await res.json();
        if (res.ok) ok += 1;
        else if (data.detail !== 'Module already active') throw new Error(data.detail || 'failed');
        else ok += 1;
      } catch (e) {
        toast.error('Could not add everything — please finish from Billing.');
        break;
      }
    }
    if (ok) {
      const names = mods.map((m) => m.name).join(' + ');
      toast.success(`${names} added — unlocking now.`);
      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: `✅ ${names} are now active. Ask me anything they cover!`,
        timestamp: new Date().toISOString(),
      }]);
    }
  };

  // One-click module add-on purchase straight from a Cory upsell card.
  const addModule = async (mod) => {
    try {
      const res = await authFetch(`${API_URL}/api/account/add-ons/purchase`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ add_on_type: 'module', module_id: mod.module_id, quantity: 1 }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'failed');
      toast.success(`${mod.name} added — $${mod.price_monthly}/mo. It's unlocking now.`);
      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: `✅ ${mod.name} is now active on your subscription. Ask me anything it covers!`,
        timestamp: new Date().toISOString(),
      }]);
    } catch (e) {
      toast.error(e.message === 'Module already active' ? 'That module is already active.' : 'Could not add the module — please try from Billing.');
    }
  };

  // Greet the logged-in STAFF MEMBER by their real first name. Prefer the
  // canonical full_name (resists first_name data-pollution where a business
  // name leaked into the field), then first_name, then a neutral fallback.
  const firstName =
    user?.full_name?.trim()?.split(/\s+/)[0] ||
    user?.first_name ||
    'there';
  if (!user) return null;

  return (
    <>
      {!open && (
        <CoryBubble
          hovered={hovered}
          setHovered={setHovered}
          onOpen={() => { setOpen(true); setProactiveHint(null); }}
          proactiveHint={proactiveHint}
        />
      )}

      {open && (
        <div
          data-testid="cory-chat-panel"
          className="fixed bottom-6 right-6 z-[60] w-[400px] max-w-[calc(100vw-2rem)] h-[600px] max-h-[calc(100vh-4rem)] rounded-3xl flex flex-col overflow-hidden"
          style={{ ...panelStyle, animation: 'corySlideUp .25s cubic-bezier(.16,1,.3,1)' }}
        >
          <style>{`
            @keyframes corySlideUp { from { opacity:0; transform:translateY(16px) scale(.97); } to { opacity:1; transform:translateY(0) scale(1); } }
            @keyframes coryDot { 0%,80%,100% { transform:scale(0); } 40% { transform:scale(1); } }
          `}</style>

          <CoryHeader
            onNewSession={startNewSession}
            onClose={() => setOpen(false)}
            status={coryStatus}
            onUpgrade={handleUpgrade}
          />

          {coryStatus?.is_trial && coryStatus?.cory_pro_via_trial && (
            <button
              type="button"
              onClick={handleUpgrade}
              data-testid="cory-trial-countdown"
              className="group flex items-center gap-2 px-4 py-2 text-left bg-gradient-to-r from-violet-600 to-indigo-600 text-white"
            >
              <Sparkles className="w-3.5 h-3.5 shrink-0" />
              <span className="text-[12px] font-medium flex-1">
                CoryPro free
                {typeof coryStatus.trial_days_left === 'number'
                  ? ` — ${coryStatus.trial_days_left} day${coryStatus.trial_days_left === 1 ? '' : 's'} left`
                  : ' during your trial'}
              </span>
              <span className="text-[11px] opacity-80 group-hover:opacity-100 underline underline-offset-2 shrink-0">Keep it</span>
            </button>
          )}

          <CoryMessageList
            messages={messages}
            loading={loading}
            firstName={firstName}
            roles={user?.roles}
            status={coryStatus}
            onUpgrade={handleUpgrade}
            onPromptClick={setInput}
            onAddModule={addModule}
            onAddBundle={addBundle}
            onDeflectionOutcome={handleDeflectionOutcome}
            deflectionAnswered={deflectionAnswered}
            scrollRef={messagesEndRef}
          />

          {showTicketForm && (
            <CoryTicketForm
              ticketData={ticketData}
              setTicketData={setTicketData}
              loading={loading}
              onSubmit={submitTicket}
              onCancel={() => setShowTicketForm(false)}
              screenshots={ticketScreenshots}
              setScreenshots={setTicketScreenshots}
            />
          )}

          <CoryInputBar
            ref={inputRef}
            input={input}
            setInput={setInput}
            loading={loading}
            onSubmit={sendMessage}
            onToggleTicket={() => setShowTicketForm((p) => !p)}
          />
        </div>
      )}
    </>
  );
}

export default CoryChat;
